import { Controller } from "@hotwired/stimulus"

// Handles preview + camera capture for bill photo uploads
export default class extends Controller {
  static targets = ["input", "preview", "video", "canvas", "status", "toggleInput", "toggleLabel"]

  connect() {
    this.stream = null
    this.setCameraToggleState("off")

    if (this.hasInputTarget && this.inputTarget.files.length > 0) {
      this.renderPreview(this.inputTarget.files[0])
    }
  }

  disconnect() {
    this.stopCamera()
  }

  handleFileSelection(event) {
    const file = event.target.files[0]
    if (file) {
      this.renderPreview(file)
      this.updateStatus("Đã chọn ảnh từ thư viện.")
    } else {
      this.clearPreview()
      this.updateStatus("Camera đang tắt. Nhấn \"Mở camera\" để chụp hóa đơn.")
    }
  }

  async startCamera() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.updateStatus("Trình duyệt không hỗ trợ mở camera.")
      return
    }

    if (this.stream) {
      this.updateStatus("Camera đã sẵn sàng. Nhấn \"Chụp hóa đơn\" để lưu ảnh.")
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      if (this.hasVideoTarget) {
        this.videoTarget.srcObject = this.stream
        await this.videoTarget.play()
      }
      this.updateStatus("Camera đã sẵn sàng. Nhấn \"Chụp hóa đơn\" để lưu ảnh.")
      this.setCameraToggleState("on")
    } catch (error) {
      console.error("Không thể khởi động camera", error)
      this.updateStatus("Không thể bật camera. Vui lòng kiểm tra quyền truy cập.")
      this.setCameraToggleState("off")
    }
  }

  toggleCameraFromSwitch(event) {
    if (event.target.checked) {
      this.startCamera()
    } else {
      this.stopCamera()
    }
  }

  capturePhoto() {
    if (!this.stream) {
      this.startCamera()
      return
    }

    if (!this.hasVideoTarget || !this.hasCanvasTarget) {
      return
    }

    const readyThreshold = typeof HTMLMediaElement === "undefined" ? 2 : HTMLMediaElement.HAVE_CURRENT_DATA
    const videoReady = this.videoTarget.readyState >= readyThreshold
    if (!videoReady) {
      this.updateStatus("Đang mở camera... vui lòng thử lại trong giây lát.")
      return
    }

    const canvas = this.canvasTarget
    canvas.width = this.videoTarget.videoWidth || 1280
    canvas.height = this.videoTarget.videoHeight || 720
    const ctx = canvas.getContext("2d")
    ctx.drawImage(this.videoTarget, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(blob => {
      if (!blob) {
        this.updateStatus("Không thể chụp ảnh. Vui lòng thử lại.")
        return
      }
      const file = new File([blob], `bill-photo-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" })
      this.attachFile(file)
      this.renderPreview(file)
      this.updateStatus("Đã chụp ảnh hóa đơn và đính kèm vào giao dịch.")
    }, "image/jpeg", 0.9)
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    if (this.hasVideoTarget) {
      this.videoTarget.pause()
      this.videoTarget.srcObject = null
    }

    this.updateStatus("Camera đang tắt. Nhấn \"Mở camera\" để chụp hóa đơn.")
    this.setCameraToggleState("off")
  }

  clearPhoto() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    this.clearPreview()
    this.updateStatus("Đã xóa ảnh hóa đơn khỏi biểu mẫu.")
  }

  attachFile(file) {
    if (!this.hasInputTarget) return
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.inputTarget.files = dataTransfer.files

    const changeEvent = new Event("change", { bubbles: true })
    this.inputTarget.dispatchEvent(changeEvent)
  }

  renderPreview(file) {
    if (!this.hasPreviewTarget) return

    this.previewTarget.innerHTML = ""

    const card = document.createElement("div")
    card.className = "card shadow-sm"

    const img = document.createElement("img")
    img.alt = "Ảnh hóa đơn"
    img.className = "card-img-top"
    const objectUrl = URL.createObjectURL(file)
    img.src = objectUrl
    img.onload = () => URL.revokeObjectURL(objectUrl)

    const body = document.createElement("div")
    body.className = "card-body py-2"
    body.innerHTML = "<p class=\"small text-muted mb-0\">Ảnh hóa đơn sẽ được lưu cùng giao dịch.</p>"

    card.appendChild(img)
    card.appendChild(body)
    this.previewTarget.appendChild(card)
  }

  clearPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ""
    }
  }

  updateStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }

  setCameraToggleState(state) {
    const hasInput = this.hasToggleInputTarget
    const hasLabel = this.hasToggleLabelTarget
    if (!hasInput && !hasLabel) return

    const isOn = state === "on"

    if (hasInput) {
      this.toggleInputTarget.checked = isOn
    }

    if (hasLabel) {
      const label = this.toggleLabelTarget
      label.classList.toggle("btn-outline-danger", isOn)
      label.classList.toggle("btn-outline-secondary", !isOn)
      label.innerHTML = isOn ? '<i class="bi bi-stop-circle me-1"></i>Tắt camera' : '<i class="bi bi-camera-video me-1"></i>Mở camera'
    }
  }
}
